/* eslint-disable array-bracket-newline */
/**
 * CEX Trading state
 * Handles balances
 */
import {
  ICEXBalanceItem,
  ICEXCheckResponse,
  ICEXExchange,
  helpers,
} from '@kattana-io/common';
import {
  CHECK_CEX,
  CHECK_CEX_DONE,
  CHECK_CEX_FAIL,
  REVOKE_CEX_KEYS,
  UPDATE_CEX_BALANCE_ITEM,
  SET_CURRENT_EXCHANGE,
} from '../actions/cex_trading';

type Exchanges = Record<string, ICEXExchange>;

export enum ECEXState {
  loading,
  enabled,
  disabled,
}

export enum ECEXExchangeType {
  unknown = 0,
  binance = 'BINANCE',
  binanceNum = 1,
  gate = 'GATE',
  gateNum = 2,
  bybit = 'BYBIT',
  bybitNum = 3,
}

export enum ECEXExchangeName {
  unknown = 'unknown',
  binance = 'binance',
  gate = 'gate',
  bybit = 'bybit',
}

export interface IExtendedCEXBalanceItem extends ICEXBalanceItem {
  exchange: ECEXExchangeType;
}

export interface ICEXTradingState {
  state: ECEXState;
  currentExchange: ECEXExchangeName;
  exchanges: Exchanges;
}

export const getExchangeNameFromType = (type: ECEXExchangeType) => {
  switch (type) {
  case ECEXExchangeType.binance:
  case ECEXExchangeType.binanceNum:
    return ECEXExchangeName.binance;
  case ECEXExchangeType.gate:
  case ECEXExchangeType.gateNum:
    return ECEXExchangeName.gate;
  case ECEXExchangeType.bybit:
  case ECEXExchangeType.bybitNum:
    return ECEXExchangeName.bybit;
  default:
    return ECEXExchangeName.unknown;
  }
};

const tryToSetNewExchangeName = (
  exchange: string,
  currentExchange: ECEXExchangeName
) => {
  switch (exchange) {
  case ECEXExchangeName.binance:
  case helpers.exchanges.BINANCE:
    return ECEXExchangeName.binance;
  case ECEXExchangeName.gate:
  case helpers.exchanges.GATEIO:
    return ECEXExchangeName.gate;
  case ECEXExchangeName.bybit:
  case helpers.exchanges.BYBIT:
    return ECEXExchangeName.bybit;
  default:
    return currentExchange;
  }
};

/**
 * By default all cexes are disabled
 */
const defaultState: ICEXTradingState = {
  state: ECEXState.disabled,
  currentExchange: ECEXExchangeName.binance,
  exchanges: {},
};

const cexTrading = (
  state: ICEXTradingState = defaultState,
  action: any
): ICEXTradingState => {
  switch (action.type) {
  case CHECK_CEX_DONE:
    return updateStateFromCheck(state, action.payload);
  case UPDATE_CEX_BALANCE_ITEM:
    return updateBalanceInState(state, action.payload);
  case CHECK_CEX_FAIL:
    return defaultState;
  case REVOKE_CEX_KEYS:
  case CHECK_CEX:
    return {
      ...state,
      state: ECEXState.loading,
    };
  case SET_CURRENT_EXCHANGE:
    return {
      ...state,
      currentExchange: tryToSetNewExchangeName(
        action.payload,
        state.currentExchange
      ),
    };
  default:
    return state;
  }
};

/**
 * Take response and put into dictionary format
 * @param state
 * @param payload
 */
const updateStateFromCheck = (
  state: ICEXTradingState,
  payload: ICEXCheckResponse
): ICEXTradingState => {
  const enabled = payload.exchanges?.some((exchange) => exchange.enable);
  return {
    ...state,
    state: enabled ? ECEXState.enabled : ECEXState.disabled,
    exchanges:
      payload.exchanges?.reduce<Exchanges>((exchanges, e) => {
        exchanges[e.name] = e;
        return exchanges;
      }, {}) || {},
  };
};

/**
 * Update balance in exchange
 * @param state
 * @param payload
 */
const updateBalanceInState = (
  state: ICEXTradingState,
  payload: IExtendedCEXBalanceItem
): ICEXTradingState => {
  const { exchange, ...balanceData } = payload;
  const exchangeName = getExchangeNameFromType(exchange);
  if (exchangeName === ECEXExchangeName.unknown) {
    return state;
  }
  return {
    ...state,
    exchanges: {
      ...state.exchanges,
      [exchangeName]: {
        ...state.exchanges[exchangeName],
        balance: [
          balanceData,
          ...(state.exchanges[exchangeName]?.balance?.filter(
            (item) => item.coin !== payload.coin
          ) || []),
        ],
      },
    },
  };
};

export default cexTrading;
